// File: `typescript/apps/front-end/src/main/react/editor/thought/ThoughtTextEditorDialog.tsx`
import React from "react";
import { CardDialog, showDialog, DialogController } from "@/main/react/Dialog/Dialog";
import ThoughtTextEditor, { Props as ThoughtTextEditorProps, EditorHandle } from "@/main/react/editor/thought/ThoughtTextEditorLazy";
import { useBackDismissesOverlay } from "@/main/navigation/useBackDismissesOverlay";
import type { Localizer } from "@/main/react/Types";

/**
 * `DISCUSSION-47` — the sheet's Back-guard host.
 *
 * <p>A component rather than a call inside the render factory, because `useBackDismissesOverlay` is a
 * hook and `showDialog` re-invokes its factory on every open/close. Declared at MODULE scope so the
 * element type is stable across those re-renders: a type declared inside the factory would be a new
 * component on every render and React would remount it, throwing away the guard's arming refs.
 *
 * <p>Why the hook and not per-surface code: this is the THIRD overlay to ship without it. The hook
 * exists because `NOTIFICATIONS-10` (2026-08-17) landed it precisely so siblings would inherit it, and
 * `SETTINGS-79` (2026-08-23) was the second. Before this, neither `CardDialog` nor `showDialog` called
 * it, so on a phone the Android Back gesture navigated OFF the discussion — and a member typically
 * arrives at `?reply=1` from a notification, so "out" is the first thing many of them want. The typed
 * text survives (`thoughtDraftStore`), but the 49,607-device-px page they were sent to does not.
 *
 * <p>Rule 33 lives entirely inside the hook: it writes history only through `NavigationHistoryState`
 * and pops only after verifying ownership.
 */
function ThoughtTextEditorSheetBackGuard({
    open,
    onRequestClose,
    marker,
    children,
}: {
    open: boolean;
    onRequestClose: () => void;
    marker: string;
    children: React.ReactNode;
}) {
    useBackDismissesOverlay({ open, onRequestClose, marker });
    return <>{children}</>;
}

/**
 * One marker per dialog instance. The marker is what `popOwnGuardEntry` reads back to prove the entry
 * it is about to pop is still ours, so two composers kept mounted on one page (the card's sheet and the
 * chain's) must not share one.
 */
let thoughtSheetGuardSequence = 0;

/**
 * Options for showing the argument editor in a dialog.
 * Accepts the same props as ThoughtTextEditor, plus optional modal title and aria id.
 */
export type ThoughtTextEditorDialogOptions = ThoughtTextEditorProps & {
    localizer: Localizer;
    title?: string;
    ariaLabelledBy?: string;
    onClose?: () => void;
    keepMounted?: boolean; // overrides the underlying showModal keepMounted (default true)
};

/**
 * showThoughtTextEditorDialog opens the ThoughtTextEditor inside a CardDialog.
 * By default it uses keepMounted:true so the same mounted modal is reused across open/close cycles.
 *
 * Returns a DialogController with { open, close, setOpen, isOpen, cleanup }.
 * Also augments the returned controller at runtime with an `addQuote(text?: string)` helper
 * so callers can imperatively insert a blockquote into the editor (useful when opening from a selection).
 */
export function showThoughtTextEditorDialog(opts: ThoughtTextEditorDialogOptions): DialogController {
    const {
        title,
        ariaLabelledBy,
        onClose,
        keepMounted = true,
        ...editorProps
    } = opts;

    // ref to call the editor's imperative handle
    const editorRef = React.createRef<EditorHandle>();

    thoughtSheetGuardSequence += 1;
    const backGuardMarker = `thoughtComposerSheet${thoughtSheetGuardSequence}`;

    const controller = showDialog(
        (cleanup, setOpen, isOpen) => {
            // onClose for CardDialog should close the dialog (keeps mounted if keepMounted=true)
            const handleClose = () => {
                setOpen(false);
                try {
                    onClose?.();
                } catch {
                    // ignore callback errors
                }
            };

            // when the argument is published, close the modal (but keep mounted) and call any provided callback
            const handlePublished = (arg: any) => {
                try {
                    editorProps.onThoughtPublished?.(arg);
                } catch {
                    // ignore callback errors
                }
                setOpen(false);
            };

            return (
                <ThoughtTextEditorSheetBackGuard
                    open={isOpen}
                    onRequestClose={handleClose}
                    marker={backGuardMarker}
                >
                    <CardDialog
                        open={isOpen}
                        title={title}
                        onClose={handleClose}
                        ariaLabelledBy={ariaLabelledBy}
                        // Mobile keeps the bottom-sheet panel (background + rounded top). From sm: up
                        // the editor card is the only frame — the outer panel would otherwise read as a
                        // second, wider card behind it. rounded-none matters because the panel keeps
                        // overflow-hidden and would clip the editor card's own corners.
                        cardClassName="py-2 sm:rounded-none sm:border-0 sm:bg-transparent sm:py-0"
                        // DISCUSSION-81 — the horizontal inset goes with the inner frame it existed to
                        // set off. Panel and editor card resolve to the same `bg-card`, so below `sm`
                        // the 8 CSS px on each side bought nothing but a stray hairline, on the
                        // narrowest composer in the product. The editor keeps its own `px-3` inside, so
                        // nothing ends up flush against the glass.
                        contentClassName="px-0 pb-2 pt-1 sm:p-0"
                    >
                        <ThoughtTextEditor
                            ref={editorRef}
                            {...(editorProps)}
                            hideAvatar={editorProps.hideAvatar ?? true}
                            autoFocus={isOpen}
                            // DISCUSSION-47 — the explicit dismiss, drawn in the header row that was
                            // 210 CSS px of empty space. Passed ONLY when there is no `title`: with a
                            // title CardDialog renders its own ✕ in the CardHeader, and two would be a
                            // duplicate. That coupling is the actual defect the row names, and this is
                            // the call-site half of breaking it.
                            onDismiss={title ? undefined : handleClose}
                            // DISCUSSION-81 — the host draws the frame below `sm`; see the prop's doc.
                            hideOwnFrameOnMobile={true}
                            onThoughtPublished={handlePublished}
                        />
                    </CardDialog>
                </ThoughtTextEditorSheetBackGuard>
            );
        },
        {
            keepMounted,
            addBodyModalOpen: true,
        }
    );

    // augment controller at runtime with addQuote helper (safe to cast)
    const augmented = controller as DialogController & { addQuote?: (text?: string) => void };

    // implement retrying addQuote so callers can call it immediately after showDialog
    // (editorRef may not be attached to the imperative handle yet when called).
    augmented.addQuote = (text?: string) => {
        if (!text) return;
        applyQuoteWhenReady(() => editorRef.current, text);
    };

    return augmented;
}

/**
 * Hand `text` to an editor's `addQuote` as soon as its imperative handle exists: polls up to 20 × 50 ms, because a
 * composer opened in the same tick (a dialog, or an inline editor behind a state change) has not attached its ref
 * yet. Shared by the dialog controller above and by the reply-quote paths (FI-584).
 */
export function applyQuoteWhenReady(
    getHandle: () => { addQuote?: (text: string) => void } | null | undefined,
    text: string,
): void {
    if (!text) return;
    const maxAttempts = 20;
    const intervalMs = 50;
    let attempts = 0;

    const tryAdd = () => {
        try {
            const handle = getHandle();
            if (handle && typeof handle.addQuote === "function") {
                try {
                    handle.addQuote(text);
                } catch {
                    // ignore errors from imperative call
                }
                return;
            }
        } catch {
            // ignore
        }
        attempts += 1;
        if (attempts < maxAttempts) {
            setTimeout(tryAdd, intervalMs);
        }
    };

    tryAdd();
}

export default showThoughtTextEditorDialog;
